import React, { useState } from 'react';
import { IconButton, Tooltip, Badge } from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import { useTranslation } from 'react-i18next';
import { unlockAudio } from '../../utils/alertSound';
import {
  notificationPermission,
  requestNotificationPermission,
  NotificationPermissionState,
} from '../../utils/desktopNotification';

export const ALERTS_ENABLED_KEY = 'pharmacy.orderAlerts.enabled';

/** Alerts default to on; only an explicit "false" turns them off. */
export function readAlertsEnabled(): boolean {
  try {
    return window.localStorage.getItem(ALERTS_ENABLED_KEY) !== 'false';
  } catch {
    return true; // private mode / storage disabled
  }
}

function writeAlertsEnabled(value: boolean): void {
  try {
    window.localStorage.setItem(ALERTS_ENABLED_KEY, String(value));
  } catch {
    /* private mode / storage disabled */
  }
}

export interface OrderAlertsToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

export const OrderAlertsToggle: React.FC<OrderAlertsToggleProps> = ({
  enabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    notificationPermission(),
  );

  const needsPermission = enabled && permission === 'default';
  // Denied outright, or a browser without the API at all (iOS Safari). Alerts
  // still work — they are just down to the banner and the tab title.
  const degraded =
    enabled && (permission === 'denied' || permission === 'unsupported');

  const handleClick = async () => {
    // In this state the button's own label reads "click to allow", so the
    // click is a grant, not a request to switch the whole feature off.
    if (needsPermission) {
      unlockAudio();
      setPermission(await requestNotificationPermission());
      return;
    }

    const next = !enabled;
    writeAlertsEnabled(next);
    onChange(next);
    if (!next) return;

    // Both audio and notifications require a user gesture; this click is it.
    unlockAudio();
    if (permission === 'default') {
      setPermission(await requestNotificationPermission());
    }
  };

  const label = !enabled
    ? t('kanban.alerts.toggleOff')
    : needsPermission
      ? t('kanban.alerts.toggleNeedsPermission')
      : degraded
        ? t('kanban.alerts.toggleDenied')
        : t('kanban.alerts.toggleOn');

  const Icon = !enabled
    ? NotificationsOffRoundedIcon
    : needsPermission
      ? NotificationsNoneRoundedIcon
      : NotificationsActiveRoundedIcon;

  const iconColor = !enabled
    ? 'text.secondary'
    : degraded
      ? 'text.disabled'
      : 'text.headline';

  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label as string}
        onClick={handleClick}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: iconColor,
          '&:hover': { bgcolor: 'primary.softBg' },
        }}
      >
        <Badge variant="dot" color="warning" invisible={!needsPermission}>
          <Icon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};
